/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

// Port of vpx-js lib/render/threejs/three-material-generator.ts.
// Changes: a MaterialSpec gives a MeshPhysicalMaterial (clearcoat); no texture maps (the
// art is drawn on canvases); a cache by material name; an unknown name gives a default.

import { Color, DoubleSide, MeshPhysicalMaterial } from "three";
import type { MaterialSpec } from "../shared/contracts.ts";

/** Brand colors. The field is black, the edge is white. */
export const BRAND = {
  field: 0x000000,
  mark: 0xff0000,
  edge: 0xffffff,
} as const;

/** Color of an item that has no material name or an unknown one. */
export const DEFAULT_COLOR = 0x888888;

/** Roughness of a spec: the roughness field, else one minus the glossiness, else 0.5. */
export function roughnessOf(spec: MaterialSpec): number {
  const value = spec.roughness ?? (spec.glossiness !== undefined ? 1 - spec.glossiness : 0.5);
  return Math.min(1, Math.max(0.04, value));
}

/** Make one physical material from a spec. */
export function createMaterial(spec: MaterialSpec): MeshPhysicalMaterial {
  const material = new MeshPhysicalMaterial({
    color: new Color(spec.baseColor),
    roughness: roughnessOf(spec),
    metalness: spec.isMetal ? 1 : 0,
    clearcoat: Math.min(1, Math.max(0, spec.clearCoat ?? 0)),
    clearcoatRoughness: 0.1,
  });
  material.name = `material:${spec.name}`;
  if (spec.isOpacityActive && spec.opacity !== undefined && spec.opacity < 1) {
    material.transparent = true;
    material.opacity = Math.min(1, Math.max(0, spec.opacity));
  }
  if (spec.emissiveColor !== undefined && (spec.emissiveIntensity ?? 0) > 0) {
    material.emissive = new Color(spec.emissiveColor);
    material.emissiveIntensity = spec.emissiveIntensity ?? 0;
  }
  return material;
}

/** Owns the materials of one table. The renderer frees them with `dispose`. */
export class MaterialLibrary {
  private readonly specs = new Map<string, MaterialSpec>();
  private readonly cache = new Map<string, MeshPhysicalMaterial>();

  constructor(specs: readonly MaterialSpec[]) {
    for (const spec of specs) this.specs.set(spec.name, spec);
  }

  /** True when the table has a material with this name. */
  has(name: string | undefined): boolean {
    return name !== undefined && this.specs.has(name);
  }

  /**
   * Get a shared material by name. An unset or unknown name gives a material of
   * `fallbackColor`. The caller must not dispose a material that this method returns.
   */
  get(name: string | undefined, fallbackColor: number = DEFAULT_COLOR): MeshPhysicalMaterial {
    const spec = name !== undefined ? this.specs.get(name) : undefined;
    const key = spec ? spec.name : `fallback:${fallbackColor}`;
    const cached = this.cache.get(key);
    if (cached) return cached;
    const material = createMaterial(
      spec ?? { name: key, baseColor: fallbackColor, roughness: 0.55 },
    );
    this.cache.set(key, material);
    return material;
  }

  /**
   * Get a shared material that draws both faces. Use it for thin parts that the camera sees
   * from either side (ramp walls). The caller must not dispose it.
   */
  getDoubleSided(
    name: string | undefined,
    fallbackColor: number = DEFAULT_COLOR,
  ): MeshPhysicalMaterial {
    const base = this.get(name, fallbackColor);
    const key = `${base.name}:double`;
    const cached = this.cache.get(key);
    if (cached) return cached;
    const material = base.clone();
    material.name = key;
    material.side = DoubleSide;
    this.cache.set(key, material);
    return material;
  }

  /** Free every material. */
  dispose(): void {
    for (const material of this.cache.values()) material.dispose();
    this.cache.clear();
  }

  /** Number of cached materials. */
  get size(): number {
    return this.cache.size;
  }
}
